/** Guest-facing delivery copy. Kitchen status and rider status are separate. */

import { resolveGuestOrderStatus, type OrderItemStatusLine } from './orderStatus';

const WAITING_KITCHEN = new Set([
  'PENDING_RESTAURANT_ACCEPTANCE',
  'ORDER_PLACED',
  'ORDER_INITIATED',
  'ORDER_DRAFT',
]);

const RIDER_ACTIVE = new Set([
  'ASSIGNED',
  'ACCEPTED',
  'AT_RESTAURANT',
  'PICKED_UP',
  'OUT_FOR_DELIVERY',
  'DELIVERED',
  'CANCELLED',
  'FAILED',
]);

const DELIVERY_FULFILLMENT_ORDER = new Set([
  'DELIVERY_ASSIGNED',
  'DELIVERY_AT_RESTAURANT',
  'ORDER_PICKED',
  'DELIVERY_PICKED',
  'DELIVERY_STARTED',
  'DELIVERY_COMPLETED',
  'DELIVERY_ORDER_COMPLETED',
  'DELIVERY_CANCELLED',
  'DELIVERY_FAILED',
  'ORDER_CANCELLED',
  'ORDER_FAILED',
]);

export const DELIVERY_PROGRESS_STEPS = [
  'Placed',
  'Confirmed',
  'Preparing',
  'Ready',
  'At restaurant',
  'Picked up',
  'On the way',
  'Delivered',
] as const;

export function isWaitingForRestaurant(orderStatus?: string): boolean {
  return WAITING_KITCHEN.has(String(orderStatus || '').toUpperCase());
}

/**
 * While the kitchen is still working, advance delivery headline/step from item
 * statuses (same as dine-in). Once a rider is involved, keep order-level status
 * so item-derived ORDER_PREPARED does not hide rider stages.
 */
export function resolveDeliveryGuestOrderStatus(
  orderStatus: string | undefined,
  deliveryStatus: string | undefined,
  items: OrderItemStatusLine[]
): string {
  const k = kitchen(orderStatus);
  const r = rider(deliveryStatus);
  if (RIDER_ACTIVE.has(r) || DELIVERY_FULFILLMENT_ORDER.has(k)) {
    return orderStatus || '';
  }
  return resolveGuestOrderStatus(orderStatus, items) || orderStatus || '';
}

function kitchen(orderStatus?: string): string {
  return String(orderStatus || '').toUpperCase();
}

function rider(deliveryStatus?: string): string {
  return String(deliveryStatus || '').toUpperCase();
}

function isUnpaidOnlineStatus(orderStatus?: string): boolean {
  const k = kitchen(orderStatus);
  return k === 'PAYMENT_INITIATED' || k === 'PAYMENT_FAILED' || k === 'PAYMENT_CANCELLED';
}

export function deliveryHeadline(orderStatus?: string, deliveryStatus?: string): string {
  const k = kitchen(orderStatus);
  const r = rider(deliveryStatus);

  if (k === 'PAYMENT_INITIATED') return 'Payment required';
  if (k === 'PAYMENT_FAILED' || k === 'PAYMENT_CANCELLED') return 'Payment failed';
  if (r === 'FAILED' || k === 'DELIVERY_FAILED') return 'Delivery failed';
  if (r === 'CANCELLED' || k === 'ORDER_CANCELLED' || k === 'DELIVERY_CANCELLED') {
    return 'Delivery cancelled';
  }
  if (r === 'DELIVERED' || k === 'DELIVERY_COMPLETED' || k === 'DELIVERY_ORDER_COMPLETED') {
    return 'Delivered';
  }
  if (r === 'AT_RESTAURANT' || k === 'DELIVERY_AT_RESTAURANT') {
    return 'At the restaurant';
  }
  if (r === 'PICKED_UP' || k === 'ORDER_PICKED' || k === 'DELIVERY_PICKED') {
    return 'Order picked';
  }
  if (r === 'OUT_FOR_DELIVERY' || k === 'DELIVERY_STARTED') {
    return 'Out for delivery';
  }
  if (r === 'ASSIGNED' || r === 'ACCEPTED' || k === 'DELIVERY_ASSIGNED') {
    return 'Rider assigned';
  }
  if (isWaitingForRestaurant(k)) return 'Waiting for the restaurant';
  if (k === 'ORDER_PREPARING') return 'Kitchen is preparing your order';
  if (k === 'ORDER_PREPARED') return 'Ready — assigning a rider';
  if (k === 'ORDER_CONFIRMED' || k === 'PAYMENT_DONE' || k === 'DELIVERY_INITIATED') {
    return 'Restaurant confirmed';
  }
  return r ? r.replace(/_/g, ' ') : 'Delivery';
}

export function deliveryHint(orderStatus?: string, deliveryStatus?: string): string {
  const k = kitchen(orderStatus);
  const r = rider(deliveryStatus);
  if (isUnpaidOnlineStatus(k)) {
    return 'Complete payment to send this order to the restaurant.';
  }
  if (isWaitingForRestaurant(k)) {
    return 'They usually confirm within a few minutes. Updates will show here.';
  }
  if (r === 'FAILED' || k === 'DELIVERY_FAILED') {
    return 'The restaurant could not complete this delivery. Check with them if you need help.';
  }
  if (r === 'CANCELLED' || k === 'ORDER_CANCELLED' || k === 'DELIVERY_CANCELLED') {
    return 'This delivery was cancelled.';
  }
  if (r === 'DELIVERED' || k === 'DELIVERY_COMPLETED' || k === 'DELIVERY_ORDER_COMPLETED') {
    return 'Hope you enjoyed your meal.';
  }
  if (r === 'AT_RESTAURANT' || k === 'DELIVERY_AT_RESTAURANT') {
    return 'The rider has arrived at the restaurant.';
  }
  if (r === 'PICKED_UP' || k === 'ORDER_PICKED' || k === 'DELIVERY_PICKED') {
    return 'The rider has picked up your order and will start the trip shortly.';
  }
  if (r === 'OUT_FOR_DELIVERY' || k === 'DELIVERY_STARTED') {
    return 'Your order is on its way.';
  }
  if (r === 'ASSIGNED' || r === 'ACCEPTED' || k === 'DELIVERY_ASSIGNED') {
    return 'A rider is heading to the restaurant to pick up your order.';
  }
  if (k === 'ORDER_PREPARING') return 'The kitchen has your order.';
  if (k === 'ORDER_PREPARED') return 'Your food is ready. A rider will be assigned shortly.';
  return 'We will keep this page updated as the order moves.';
}

/**
 * 0 placed, 1 confirmed, 2 preparing, 3 ready, 4 at restaurant,
 * 5 picked, 6 out for delivery, 7 delivered, -1 cancelled/failed
 */
export function deliveryStepIndex(orderStatus?: string, deliveryStatus?: string): number {
  const k = kitchen(orderStatus);
  const r = rider(deliveryStatus);
  if (
    isUnpaidOnlineStatus(k) ||
    r === 'FAILED' ||
    r === 'CANCELLED' ||
    k === 'ORDER_CANCELLED' ||
    k === 'DELIVERY_CANCELLED' ||
    k === 'DELIVERY_FAILED'
  ) {
    return -1;
  }
  if (r === 'DELIVERED' || k === 'DELIVERY_COMPLETED' || k === 'DELIVERY_ORDER_COMPLETED') {
    return 7;
  }
  if (r === 'AT_RESTAURANT' || k === 'DELIVERY_AT_RESTAURANT') {
    return 4;
  }
  if (r === 'PICKED_UP' || k === 'ORDER_PICKED' || k === 'DELIVERY_PICKED') {
    return 5;
  }
  if (r === 'OUT_FOR_DELIVERY' || k === 'DELIVERY_STARTED') {
    return 6;
  }
  if (r === 'ASSIGNED' || r === 'ACCEPTED' || k === 'DELIVERY_ASSIGNED' || k === 'ORDER_PREPARED') {
    return 3;
  }
  if (k === 'ORDER_PREPARING') {
    return 2;
  }
  if (
    k === 'ORDER_CONFIRMED' ||
    k === 'PAYMENT_DONE' ||
    k === 'DELIVERY_INITIATED'
  ) {
    return 1;
  }
  return 0;
}

export function isTerminalRiderStatus(deliveryStatus?: string): boolean {
  const r = rider(deliveryStatus);
  return r === 'CANCELLED' || r === 'FAILED' || r === 'DELIVERED';
}

export function keepFreshDeliveryTrackingStatus(
  local?: string,
  incoming?: string
): string | undefined {
  const current = local != null && local !== '' ? String(local) : undefined;
  const next = incoming != null && incoming !== '' ? String(incoming) : undefined;
  if (!next) return current;
  if (!current) return next;
  if (isTerminalRiderStatus(current) && !isTerminalRiderStatus(next)) {
    return current;
  }
  return next;
}
